import { bindAjaxCartForms } from "~/core/cart-drawer/bind-ajax-cart-forms";
import { makeEventNamespace } from "~/core/dom/events";
import { initSection } from "~/core/shopify/init-section";
import {
    SYSTEM_SELECT_EVENT,
    getSystemRows,
    selectSystemRow,
    selectedRow,
} from "~/scripts/sections/system-rows";
import "~/styles/sections/system-finder.css";

type Cleanup = () => void;

/*
 * The row this section shows below 1024px. Tapping it opens it and closes the others; the
 * Products and Benefits tabs switch panels; Add to cart gets the PDP shimmer while in view.
 */
const initSystemRow = (row: HTMLElement): Cleanup => {
    const toggle = row.querySelector<HTMLButtonElement>(
        "[data-system-row-toggle]",
    );
    const body = row.querySelector<HTMLElement>(".system-row__body");
    const tabs = Array.from(
        row.querySelectorAll<HTMLButtonElement>("[data-system-row-tab]"),
    );
    const cleanups: Cleanup[] = [];

    const setOpen = (open: boolean): void => {
        row.classList.toggle("is-open", open);
        toggle?.setAttribute("aria-expanded", String(open));
        if (body !== null) body.inert = !open;
    };

    const onToggle = (): void => selectSystemRow(row);
    toggle?.addEventListener("click", onToggle);
    cleanups.push(() => toggle?.removeEventListener("click", onToggle));

    const onSelect = (event: Event): void => {
        const target = selectedRow(event);
        if (target !== null) setOpen(target === row);
    };
    document.addEventListener(SYSTEM_SELECT_EVENT, onSelect);
    cleanups.push(() =>
        document.removeEventListener(SYSTEM_SELECT_EVENT, onSelect),
    );

    const selectTab = (index: number, focus = false): void => {
        tabs.forEach((tab, tabIndex) => {
            const active = tabIndex === index;
            tab.setAttribute("aria-selected", String(active));
            tab.tabIndex = active ? 0 : -1;
            const panelId = tab.getAttribute("aria-controls");
            const panel = panelId ? document.getElementById(panelId) : null;
            if (panel !== null) panel.hidden = !active;
            if (active && focus) tab.focus();
        });
    };

    tabs.forEach((tab, index) => {
        const onClick = (): void => selectTab(index);
        const onKey = (event: KeyboardEvent): void => {
            if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
            event.preventDefault();
            const step = event.key === "ArrowRight" ? 1 : -1;
            selectTab((index + step + tabs.length) % tabs.length, true);
        };
        tab.addEventListener("click", onClick);
        tab.addEventListener("keydown", onKey);
        cleanups.push(() => {
            tab.removeEventListener("click", onClick);
            tab.removeEventListener("keydown", onKey);
        });
    });

    const add = row.querySelector<HTMLElement>(".system-row__add");
    if (
        add !== null &&
        "IntersectionObserver" in window &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
        const observer = new IntersectionObserver(
            ([entry]) => {
                add.classList.toggle(
                    "is-shimmer-active",
                    entry?.isIntersecting === true,
                );
            },
            { threshold: 0.2 },
        );
        observer.observe(add);
        cleanups.push(() => observer.disconnect());
    }

    // The first system renders open. If the section order changed and none did, open the first.
    const rows = getSystemRows();
    if (rows[0] === row && !rows.some((item) => item.classList.contains("is-open"))) {
        selectSystemRow(row);
    }

    return () => cleanups.forEach((cleanup) => cleanup());
};

const nearestIndex = (scroller: HTMLElement, items: HTMLElement[]): number => {
    const target = scroller.scrollLeft + scroller.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    items.forEach((item, index) => {
        const distance = Math.abs(
            item.offsetLeft + item.offsetWidth / 2 - target,
        );
        if (distance < closestDistance) {
            closestIndex = index;
            closestDistance = distance;
        }
    });

    return closestIndex;
};

initSection(".js-system-card-section", (section) => {
    const namespace = makeEventNamespace();
    bindAjaxCartForms(namespace, section);
    const row = section.querySelector<HTMLElement>("[data-system-row]");
    const cleanupRow = row !== null ? initSystemRow(row) : null;
    const gallery = section.querySelector<HTMLElement>(
        "[data-system-gallery]",
    );
    const track = gallery?.querySelector<HTMLElement>(
        "[data-system-gallery-track]",
    );
    const slides = Array.from(
        gallery?.querySelectorAll<HTMLElement>("[data-system-gallery-slide]") ??
            [],
    );
    const dots = Array.from(
        gallery?.querySelectorAll<HTMLButtonElement>(
            "[data-system-gallery-dot]",
        ) ?? [],
    );
    let frame: number | null = null;

    const selectImage = (index: number): void => {
        dots.forEach((dot, dotIndex) => {
            const active = dotIndex === index;
            dot.classList.toggle("is-active", active);
            dot.setAttribute("aria-pressed", String(active));
        });
    };

    if (track !== undefined && track !== null && slides.length > 1) {
        namespace.addDirectEventListener(
            track,
            "scroll",
            () => {
                if (frame !== null) return;
                frame = window.requestAnimationFrame(() => {
                    selectImage(nearestIndex(track, slides));
                    frame = null;
                });
            },
            { passive: true },
        );

        dots.forEach((dot) => {
            namespace.addDirectEventListener(dot, "click", () => {
                const index = Number(dot.dataset.systemGalleryDot);
                const slide = slides[index];
                if (slide === undefined) return;
                track.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
                selectImage(index);
            });
        });
    }

    return {
        unload: () => {
            if (frame !== null) window.cancelAnimationFrame(frame);
            cleanupRow?.();
            namespace.destroy();
        },
    };
});
